import { Injectable, inject } from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { BehaviorSubject, Observable } from 'rxjs'
import { ActivatedRoute, Router } from '@angular/router'
import { RouterQueryParams } from '../../../shared/router-query-params.service'
import { filter, map } from 'rxjs/operators'
import { Interval, TimeUtil } from '@elderbyte/ngx-starter'
import { LocalDate } from '@js-joda/core'

@Injectable({
  providedIn: 'root',
})
export class IntervalContextService {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)
  private routerQueryParams = inject(RouterQueryParams)
  private readonly _activeInterval = new BehaviorSubject<Interval>(
    Interval.ofDayDate(TimeUtil.localDateToDate(LocalDate.now()))
  )

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    // Check for interval query param on all router levels
    this.routerQueryParams.params
      .pipe(
        map((params) => params['interval']),
        filter((intervalParam) => !!intervalParam),
        map((intervalParam) => Interval.parse(intervalParam))
      )
      .subscribe({
        next: (interval) => this.setInterval(interval, false),
        error: (err) => this.log.error('Failed to set interval from query params!', err),
      })
  }

  /***************************************************************************
   *                                                                         *
   * Company Getters / Setters                                               *
   *                                                                         *
   **************************************************************************/

  public get interval$(): Observable<Interval> {
    return this._activeInterval.pipe(filter((interval) => !!interval))
  }

  public get interval(): Interval | null {
    return this._activeInterval.getValue()
  }

  public setInterval(interval: Interval, updateQueryParam = true): void {
    if (!this.intervalEquals(this.interval, interval)) {
      this._activeInterval.next(interval)
      if (updateQueryParam) {
        this.updateIntervalQueryParam(interval)
      }
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private intervalEquals(before: Interval, after: Interval): boolean {
    if (before && after) {
      return before.equals(after)
    } else {
      return before === after
    }
  }

  private updateIntervalQueryParam(interval: Interval): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { interval: interval.toInstantString() },
      replaceUrl: true, // if true, won't add a new browser history entry
      queryParamsHandling: 'merge',
    })
  }
}
